import { inject } from '@angular/core';
import { RedirectCommand, ResolveFn, Router } from '@angular/router';
import { map } from 'rxjs';
import { SessionStatusEnum } from 'src/app/@data-services/event-agenda/event-agenda.data-model';
import { EventAgendaDataService } from 'src/app/@data-services/event-agenda/event-agenda.data-service';
import { EventConfigStateService } from 'src/app/@store/event-config-state.service';

/**
 * SYN-2365: Inverse of determineSessionStatusResolver. Guards the Debrief
 * routes so that an attendee who lands on /session/:id/debrief (refresh or
 * direct URL) while the session has been restarted to live is sent to the
 * live insights view instead of being shown the stale Debrief page.
 */
export const determineDebriefStatusResolver: ResolveFn<boolean> = (
  route,
  _state
) => {
  if (route.queryParamMap.get('internal')) {
    return true;
  }

  const router = inject(Router);
  const eventAgendaDataService = inject(EventAgendaDataService);
  const eventConfigStateService = inject(EventConfigStateService);

  // Mirror determineSessionStatusResolver: hydrate the primary-screen flag
  // from the URL so direct/refresh loads of /debrief?isPrimaryScreen=... land
  // with the store already in the correct mode, and so an explicit
  // ?isPrimaryScreen=false also clears the flag (the component's own ngOnInit
  // only handles the truthy case).
  const isPrimaryScreenParam = route.queryParamMap.get('isPrimaryScreen');
  if (isPrimaryScreenParam !== null) {
    eventConfigStateService.setPrimaryScreen(isPrimaryScreenParam === 'true');
  }

  return eventAgendaDataService
    .getEventDetails({
      event: eventConfigStateService.$eventIdentifier(),
    })
    .pipe(
      map((response) => {
        const sessionId = route.paramMap.get('sessionId') ?? '';
        const selectedSession = response.data.find(
          (e) => e.PrimarySessionId === sessionId
        );
        if (
          selectedSession?.Status === SessionStatusEnum.InProgress ||
          selectedSession?.Status === SessionStatusEnum.NotStarted
        ) {
          return new RedirectCommand(
            // Carry `internal` so the live resolver's escape hatch fires and it
            // can't bounce us back to debrief — the matching guard to the one in
            // determine-session-status.resolver. `merge` preserves existing
            // params (isPrimaryScreen / language) while adding internal.
            router.createUrlTree(['session', sessionId], {
              queryParams: { internal: '1' },
              queryParamsHandling: 'merge',
            }),
            { skipLocationChange: true }
          );
        }
        return true;
      })
    );
};
